// Toptancı Tam Raporu (Excel + PDF) — günlük yedek amaçlı.
// Tüm toptancılar, tüm borç/alacak kayıtları (arşivliler dahil) ve tüm ödeme geçmişi tek dosyada.
// Bakiye hesabı WholesalerPayments.js wholesalerGroups ile birebir aynı: arşivlenmemiş kayıtlar,
// Net = Kalan Borç − Açık Alacak (pozitif → borçluyuz, negatif → alacaklıyız).
import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const fmtNum = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const stamp = () => {
  const n = new Date();
  const p = (x) => String(x).padStart(2, '0');
  return `${n.getFullYear()}-${p(n.getMonth() + 1)}-${p(n.getDate())}_${p(n.getHours())}-${p(n.getMinutes())}`;
};

const parsePhotos = (photos) => {
  if (!photos) return [];
  if (Array.isArray(photos)) return photos;
  try { const a = JSON.parse(photos); return Array.isArray(a) ? a : []; } catch (e) { return []; }
};

// Ham API verisinden rapor modelini kur
// labels: { currencyInfo, statusNames, paymentMethodInfo }
export function buildWholesalerReportModel(raw, labels) {
  const { wholesalers = [], payments = [], history = [] } = raw;
  const { currencyInfo = {}, statusNames = {}, paymentMethodInfo = {} } = labels;
  // TL için sembol yerine "TL" (PDF fontunda ₺ glifi yok; Excel'de de tutarlı kalsın)
  const unit = (c) => ((c || 'TRY') === 'TRY' ? 'TL' : currencyInfo[c]?.symbol || c);

  const histByPayment = new Map();
  history.forEach(h => {
    if (!histByPayment.has(h.payment_id)) histByPayment.set(h.payment_id, []);
    histByPayment.get(h.payment_id).push(h);
  });

  const groupsDef = [
    ...wholesalers.map(w => ({ id: w.id, name: w.name || '', company: w.company_name || '', w })),
    { id: null, name: 'Diğer (toptancısız)', company: '', w: null }
  ];

  const groups = groupsDef.map(g => {
    const gPayments = payments.filter(p => (g.id === null ? !p.wholesaler_id : p.wholesaler_id === g.id));
    const balances = {};
    gPayments.filter(p => !p.archived).forEach(p => {
      const c = p.currency || 'TRY';
      if (!balances[c]) balances[c] = { total: 0, paid: 0, remaining: 0, credit: 0, net: 0, openCount: 0 };
      const total = Number(p.amount) || 0;
      const isPaid = p.status === 'paid';
      const paidAmt = isPaid ? total : (Number(p.paid_amount) || 0);
      const openAmt = isPaid ? 0 : Math.max(0, total - paidAmt);
      if (p.payment_type === 'income') {
        balances[c].credit += openAmt;
      } else {
        balances[c].total += total;
        balances[c].paid += paidAmt;
        balances[c].remaining += openAmt;
      }
      if (p.status === 'pending' || p.status === 'partial') balances[c].openCount += 1;
    });
    Object.values(balances).forEach(b => { b.net = round2(b.remaining - b.credit); });

    const gHistory = gPayments.flatMap(p => (histByPayment.get(p.id) || []).map(h => ({ ...h, payment: p })))
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const lastPay = gHistory.length ? gHistory[gHistory.length - 1] : null;

    // Kronolojik hareket listesi (kayıt açılışı + her ödeme)
    const movements = [
      ...gPayments.map(p => ({
        date: p.created_at || p.due_date,
        kind: p.payment_type === 'income' ? 'Alacak Kaydı' : 'Borç Kaydı',
        title: p.title || '',
        currency: p.currency || 'TRY',
        debit: p.payment_type === 'income' ? 0 : Number(p.amount) || 0,
        credit: p.payment_type === 'income' ? Number(p.amount) || 0 : 0,
        info: [statusNames[p.status] || p.status, p.due_date ? `Vade ${fmtDate(p.due_date)}` : '', p.archived ? 'Arşivde' : '', p.invoice_issued ? 'Faturalı' : ''].filter(Boolean).join(' · ')
      })),
      ...gHistory.map(h => ({
        date: h.created_at,
        kind: h.payment.payment_type === 'income' ? 'Alacak Tahsili' : 'Ödeme',
        title: `${h.payment.title || ''}${h.note ? ` — ${h.note}` : ''}`,
        currency: h.payment.currency || 'TRY',
        debit: 0,
        credit: Number(h.amount) || 0,
        info: paymentMethodInfo[h.payment_method]?.name || h.payment_method || ''
      }))
    ].sort((a, b) => new Date(a.date) - new Date(b.date));

    return { ...g, payments: gPayments, history: gHistory, balances, lastPay, movements };
  }).filter(g => g.payments.length > 0 || g.w);

  return { groups, unit, statusNames, paymentMethodInfo, histByPayment };
}

const netLabel = (net) => (net > 0.004 ? 'Borçluyuz' : net < -0.004 ? 'Alacaklıyız' : 'Kapalı');

// ===================== EXCEL =====================
const HEAD = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0F766E' } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: { bottom: { style: 'thin', color: { rgb: '94A3B8' } } } };
const TITLE = { font: { bold: true, sz: 14, color: { rgb: '0F172A' } } };
const NUM = '#,##0.00';

const makeSheet = (title, columns, rows, numberCols, widths) => {
  const aoa = [[{ v: title, t: 's', s: TITLE }], [{ v: `Oluşturulma: ${fmtDateTime(new Date())}`, t: 's' }], [], columns.map(c => ({ v: c, t: 's', s: HEAD }))];
  rows.forEach(r => {
    aoa.push(r.map((v, i) => {
      if (v === null || v === undefined || v === '') return { v: '', t: 's' };
      if (typeof v === 'number') return { v, t: 'n', z: numberCols.includes(i) ? NUM : undefined };
      return { v: String(v), t: 's' };
    }));
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = widths.map(w => ({ wch: w }));
  ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 3, c: 0 }, e: { r: 3 + rows.length, c: columns.length - 1 } }) };
  ws['!freeze'] = { xSplit: 0, ySplit: 4 };
  ws['!views'] = [{ state: 'frozen', ySplit: 4 }];
  return ws;
};

export function exportWholesalerReportExcel(model) {
  const { groups, unit, statusNames, paymentMethodInfo } = model;
  const wb = XLSX.utils.book_new();

  // 1) Özet — toptancı × birim
  const summaryRows = [];
  groups.forEach(g => {
    const currs = Object.keys(g.balances);
    const lastPay = g.lastPay;
    const lastCols = [lastPay ? fmtDateTime(lastPay.created_at) : '', lastPay ? Number(lastPay.amount) || 0 : '', lastPay ? unit(lastPay.payment.currency) : ''];
    if (currs.length === 0) {
      summaryRows.push([g.name, g.company, g.w?.phone || '', '', '', '', '', '', '', 'Kayıt yok', ...lastCols, 0]);
      return;
    }
    currs.forEach(c => {
      const b = g.balances[c];
      summaryRows.push([g.name, g.company, g.w?.phone || '', unit(c), round2(b.total), round2(b.paid), round2(b.remaining), round2(b.credit), Math.abs(b.net), netLabel(b.net), ...lastCols, b.openCount]);
    });
  });
  XLSX.utils.book_append_sheet(wb, makeSheet('Toptancı Bakiye Özeti',
    ['Toptancı', 'Firma', 'Telefon', 'Birim', 'Toplam Borç', 'Ödenen', 'Kalan Borç', 'Açık Alacak', 'Net Bakiye', 'Durum', 'Son Ödeme Tarihi', 'Son Ödeme Tutarı', 'Son Ödeme Birimi', 'Açık Kayıt'],
    summaryRows, [4, 5, 6, 7, 8, 11], [26, 22, 15, 14, 14, 14, 14, 14, 14, 12, 18, 14, 14, 10]), 'Özet');

  // 2) Kayıtlar — her borç/alacak kaydı
  const recordRows = [];
  groups.forEach(g => g.payments.forEach(p => {
    const total = Number(p.amount) || 0;
    const paid = p.status === 'paid' ? total : (Number(p.paid_amount) || 0);
    recordRows.push([
      g.name, p.id, fmtDateTime(p.created_at), p.due_date ? fmtDate(p.due_date) : '',
      p.payment_type === 'income' ? 'Alacak' : 'Borç', p.title || '', unit(p.currency),
      total, round2(paid), round2(Math.max(0, total - paid)),
      statusNames[p.status] || p.status || '', p.invoice_issued ? 'Kesildi' : '', p.archived ? 'Evet' : '',
      p.notes || '', parsePhotos(p.photos).length || '', p.created_by_name || ''
    ]);
  }));
  XLSX.utils.book_append_sheet(wb, makeSheet('Borç / Alacak Kayıtları',
    ['Toptancı', 'Kayıt No', 'Kayıt Tarihi', 'Vade', 'Tür', 'Açıklama', 'Birim', 'Tutar', 'Ödenen', 'Kalan', 'Durum', 'Fatura', 'Arşivde', 'Notlar', 'Fotoğraf', 'Oluşturan'],
    recordRows, [7, 8, 9], [24, 9, 17, 11, 8, 34, 13, 13, 13, 13, 12, 9, 8, 26, 9, 14]), 'Kayıtlar');

  // 3) Ödeme geçmişi — her ödeme hareketi
  const histRows = [];
  groups.forEach(g => g.history.forEach(h => {
    histRows.push([
      g.name, fmtDateTime(h.created_at), h.payment_id, h.payment.title || '',
      h.payment.payment_type === 'income' ? 'Alacak tahsili' : 'Borç ödemesi', unit(h.payment.currency),
      Number(h.amount) || 0, paymentMethodInfo[h.payment_method]?.name || h.payment_method || '',
      h.invoice_issued ? 'Kesildi' : '', h.note || ''
    ]);
  }));
  XLSX.utils.book_append_sheet(wb, makeSheet('Ödeme Geçmişi',
    ['Toptancı', 'Tarih', 'Kayıt No', 'Kayıt Açıklaması', 'İşlem', 'Birim', 'Tutar', 'Yöntem', 'Fatura', 'Not'],
    histRows, [6], [24, 17, 9, 34, 14, 13, 13, 12, 9, 30]), 'Ödeme Geçmişi');

  // 4) Toptancı bilgileri
  const infoRows = groups.filter(g => g.w).map(g => {
    const w = g.w;
    return [w.code || '', w.name || '', w.company_name || '', w.contact_person || '', w.phone || '', w.email || '', w.address || '', w.tax_number || '', w.bank_name || '', w.iban || '', w.notes || '', w.active === 0 ? 'Pasif' : 'Aktif'];
  });
  XLSX.utils.book_append_sheet(wb, makeSheet('Toptancı Bilgileri',
    ['Kod', 'Ad', 'Firma', 'Yetkili', 'Telefon', 'E-posta', 'Adres', 'Vergi No', 'Banka', 'IBAN', 'Notlar', 'Durum'],
    infoRows, [], [10, 24, 24, 18, 15, 22, 30, 14, 16, 28, 26, 8]), 'Toptancılar');

  XLSX.writeFile(wb, `toptanci-tam-rapor_${stamp()}.xlsx`);
}

// ===================== PDF =====================
// Helvetica ş/ğ/ı/İ basamıyor → Türkçe destekli Liberation Sans (public/fonts) sadece PDF üretilirken yüklenir
const FONT = 'LiberationSans';
const loadFont = async (doc) => {
  const toB64 = (buf) => {
    let bin = '';
    const bytes = new Uint8Array(buf);
    for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    return btoa(bin);
  };
  const base = process.env.PUBLIC_URL || '';
  const [reg, bold] = await Promise.all([
    fetch(`${base}/fonts/LiberationSans-Regular.ttf`).then(r => { if (!r.ok) throw new Error('font'); return r.arrayBuffer(); }),
    fetch(`${base}/fonts/LiberationSans-Bold.ttf`).then(r => { if (!r.ok) throw new Error('font'); return r.arrayBuffer(); })
  ]);
  doc.addFileToVFS('LiberationSans-Regular.ttf', toB64(reg));
  doc.addFont('LiberationSans-Regular.ttf', FONT, 'normal');
  doc.addFileToVFS('LiberationSans-Bold.ttf', toB64(bold));
  doc.addFont('LiberationSans-Bold.ttf', FONT, 'bold');
  doc.setFont(FONT, 'normal');
};

const TEAL = [15, 118, 110];

export async function exportWholesalerReportPdf(model) {
  const { groups, unit } = model;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  await loadFont(doc);
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = { left: 28, right: 28, top: 60, bottom: 34 };
  const created = fmtDateTime(new Date());

  const decorate = () => {
    doc.setFillColor(...TEAL);
    doc.rect(0, 0, pageW, 44, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont(FONT, 'bold');
    doc.setFontSize(15);
    doc.text('AKIK — Toptancı Tam Raporu', 28, 28);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(9);
    doc.text(`Oluşturulma: ${created}`, pageW - 28, 28, { align: 'right' });
    doc.setTextColor(120);
    doc.setFontSize(8);
    doc.text(`Sayfa ${doc.internal.getCurrentPageInfo().pageNumber}`, pageW - 28, pageH - 14, { align: 'right' });
  };
  const tableBase = {
    margin,
    theme: 'grid',
    styles: { font: FONT, fontSize: 8, cellPadding: 3, lineColor: [203, 213, 225], lineWidth: 0.3, textColor: [30, 41, 59], overflow: 'linebreak' },
    headStyles: { font: FONT, fontStyle: 'bold', fillColor: [51, 65, 85], textColor: [255, 255, 255], halign: 'center' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    didDrawPage: decorate
  };

  // 1) Genel özet
  const summaryBody = [];
  groups.forEach(g => {
    const currs = Object.keys(g.balances);
    const last = g.lastPay ? `${fmtDate(g.lastPay.created_at)} · ${fmtNum(g.lastPay.amount)} ${unit(g.lastPay.payment.currency)}` : '—';
    if (currs.length === 0) { summaryBody.push([g.name, '—', '', '', '', '', 'Kayıt yok', last]); return; }
    currs.forEach((c, i) => {
      const b = g.balances[c];
      summaryBody.push([i === 0 ? g.name : '', unit(c), fmtNum(b.total), fmtNum(b.paid), fmtNum(b.remaining), fmtNum(b.credit), `${fmtNum(Math.abs(b.net))} (${netLabel(b.net)})`, i === 0 ? last : '']);
    });
  });
  autoTable(doc, {
    ...tableBase,
    startY: margin.top,
    head: [['Toptancı', 'Birim', 'Toplam Borç', 'Ödenen', 'Kalan Borç', 'Açık Alacak', 'Net Bakiye', 'Son Ödeme']],
    body: summaryBody,
    columnStyles: { 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } }
  });

  // 2) Her toptancı için ayrıntılı hareket dökümü
  // Her toptancı yeni bölüm; sayfada yeterli yer varsa (≈160pt) aynı sayfada devam eder
  doc.addPage();
  let y = margin.top;
  groups.filter(g => g.payments.length > 0).forEach((g, gi) => {
    if (gi > 0) {
      y = doc.lastAutoTable.finalY + 26;
      if (y > pageH - margin.bottom - 160) {
        doc.addPage();
        y = margin.top;
      } else {
        doc.setDrawColor(...TEAL);
        doc.setLineWidth(1);
        doc.line(margin.left, y - 12, pageW - margin.right, y - 12);
      }
    }
    doc.setTextColor(15, 23, 42);
    doc.setFont(FONT, 'bold');
    doc.setFontSize(13);
    doc.text(`${g.name}${g.company ? ` — ${g.company}` : ''}`, margin.left, y + 4);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    const contact = g.w ? [g.w.contact_person, g.w.phone, g.w.email, g.w.iban ? `IBAN ${g.w.iban}` : ''].filter(Boolean).join(' · ') : '';
    if (contact) doc.text(contact, margin.left, y + 17);
    y += contact ? 26 : 14;

    const balBody = Object.entries(g.balances).map(([c, b]) => [unit(c), fmtNum(b.total), fmtNum(b.paid), fmtNum(b.remaining), fmtNum(b.credit), `${fmtNum(Math.abs(b.net))} (${netLabel(b.net)})`, String(b.openCount)]);
    if (balBody.length) {
      autoTable(doc, {
        ...tableBase,
        startY: y,
        head: [['Birim', 'Toplam Borç', 'Ödenen', 'Kalan Borç', 'Açık Alacak', 'Net Bakiye', 'Açık Kayıt']],
        body: balBody,
        tableWidth: 560,
        columnStyles: { 1: { halign: 'right' }, 2: { halign: 'right' }, 3: { halign: 'right' }, 4: { halign: 'right' }, 5: { halign: 'right', fontStyle: 'bold' }, 6: { halign: 'center' } }
      });
      y = doc.lastAutoTable.finalY + 6;
    }
    doc.setFontSize(8.5);
    doc.setTextColor(71, 85, 105);
    doc.text(`Son ödeme: ${g.lastPay ? `${fmtDateTime(g.lastPay.created_at)} — ${fmtNum(g.lastPay.amount)} ${unit(g.lastPay.payment.currency)}` : 'yok'}`, margin.left, y + 8);
    y += 16;

    autoTable(doc, {
      ...tableBase,
      startY: y,
      head: [['Tarih', 'İşlem', 'Açıklama', 'Birim', 'Borç', 'Ödeme / Alacak', 'Bilgi']],
      body: g.movements.map(m => [fmtDateTime(m.date), m.kind, m.title, unit(m.currency), m.debit ? fmtNum(m.debit) : '', m.credit ? fmtNum(m.credit) : '', m.info]),
      columnStyles: { 0: { cellWidth: 82 }, 1: { cellWidth: 72 }, 3: { cellWidth: 70 }, 4: { halign: 'right', cellWidth: 72, textColor: [185, 28, 28] }, 5: { halign: 'right', cellWidth: 78, textColor: [21, 128, 61] }, 6: { cellWidth: 150 } }
    });
  });

  doc.save(`toptanci-tam-rapor_${stamp()}.pdf`);
}
